// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import {useIntl} from 'react-intl'
import classNames from 'classnames'
import { IAttachment, IAttachmentStatus } from '../../modules/media/Media.model'
import { CustomInputRenderer } from './CustomInputRenderer'
import {get} from 'lodash'
import { FormikProps } from 'formik'
import { sizeFormater } from './FilePicker'

interface Props {
  disabled?: boolean
  attachment: IAttachment
  formik?: FormikProps<any>
  fieldName?: string
  deleteConfirmationModalId?: string
  closeOnClickHandler: () => void
  deleteOnClickHandler?: (id: string) => void
  retryOnClickHandler?: (id: string) => void
  downloadOnClickHandler?: (id: string) => void
}

const FilePickedPreview: FC<Props> = ({
  disabled,
  attachment,
  formik,
  fieldName,
  deleteConfirmationModalId = 'attachment_delete_confirmation_modal',
  closeOnClickHandler,
  deleteOnClickHandler,
  retryOnClickHandler,
  downloadOnClickHandler,
}: Props) => {
  const intl = useIntl()

  const truncateDescription = (length: number, detail: string) => {
    if (detail && detail.length > length) {
      return `${detail.substring(0, length)}...`
    } else {
      return detail
    }
  }

  const renderTextArea = (attachment: IAttachment, formik?: FormikProps<any>, fieldName?: string) => {
    if (attachment && (!formik || !fieldName)) {
      return (
        <div className='row'>
          <label className='col-xxl-3 fs-6 text-gray-700 col-form-label'>
            {intl.formatMessage({id: 'UTIL.COMMENTS'})}
          </label>
          <div className='col-xxl-9'>
            <textarea
              rows={attachment.comments ? 5 : 2}
              className='form-control form-control-sm form-control-solid px-5 py-5 fs-6 text-dark'
              disabled={true}
              value={attachment.comments?.toUpperCase() ?? ''}
            />
            <div className='d-flex flex-row p-1'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='d-flex flex-row flex-column-fluid'>
                  {attachment && attachment.comments && attachment.comments.length > 0 && (
                    <div className='d-flex flex-row-auto flex-end align-items-start'>
                      <span className='fs-8 text-muted'>{`${
                        attachment.comments.length || 0
                      }/3000`}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (formik && fieldName) {
      let index = undefined
      const files = get(formik.values, fieldName)
      files && files.find((file, i) => {
        if (file.id === attachment.id) {
          index = i
          return true
        }
        return false
      })

      const pathStatus = `${fieldName}.${index}.status`
      const pathComments = `${fieldName}.${index}.comments`
      const statusValue = formik ? get(formik?.values, pathStatus) : ''
      const commentsValue: string = formik ? get(formik?.values, pathComments) : ''

      return (
        <div className='row'>
          <label className='col-xxl-3 fs-6 text-gray-700 col-form-label'>
            {intl.formatMessage({id: 'UTIL.COMMENTS'})}
          </label>
          <div className='col-xxl-9'>
            <textarea
              rows={get(formik?.values, `${pathComments}`) ? 5 : 2}
              className='form-control form-control-sm form-control-solid px-5 py-5 fs-6 text-dark'
              {...formik?.getFieldProps(pathComments)}
              placeholder={intl.formatMessage({id: 'UTIL.COMMENTS'})}
              onChange={(e) => {
                if (statusValue === IAttachmentStatus.SAVED) {
                  formik?.setFieldValue(pathStatus, IAttachmentStatus.UPDATE)
                }
                formik?.handleChange(e)
              }}
              disabled={disabled}
              value={commentsValue?.toUpperCase() ?? ''}
            />
            <div className='d-flex flex-row p-1'>
              <div className='d-flex flex-column flex-row-fluid'>
                <div className='d-flex flex-row flex-column-fluid'>
                  <div className='d-flex flex-row-fluid flex-start'>
                    {formik &&
                      get(formik.touched, `${pathComments}`) &&
                      get(formik.errors, `${pathComments}`) && (
                        <span className='fs-8 text-danger'>
                          {get(formik.errors, `${pathComments}`)?.toUpperCase() ?? ''}
                        </span>
                      )}
                  </div>

                  {formik && get(formik?.values, `${pathComments}`)?.length > 0 && (
                    <div className='d-flex flex-row-auto flex-end align-items-start'>
                      <span className='fs-8 text-muted'>{`${
                        get(formik?.values, `${pathComments}`)?.length || 0
                      }/3000`}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  return (
    <>
      <div
        className={classNames('col-12', 'col-lg-7', {
          'col-12': attachment,
        })}
      >
        <div className='card card-flush card-stretch-100 card-bordered'>
          <div className='card-body'>
            <div className='d-flex flex-row flex-column-fluid align-items-center flex-end ms-5'>
              {!disabled && (
                <div className='d-flex flex-row-aut'>
                  <>
                    {formik && attachment.uploaded && attachment.uploadUrl && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger me-3'
                        onClick={() =>
                          attachment && deleteOnClickHandler && deleteOnClickHandler(attachment.id)
                        }
                      >
                        <i className='bi bi-trash fw-bold fs-3'></i>
                      </button>
                    )}
                    {formik && attachment.path && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger me-3'
                        data-bs-toggle='modal'
                        data-bs-target={`#${deleteConfirmationModalId}`}
                      >
                        <i className='bi bi-trash fw-bold fs-3'></i>
                      </button>
                    )}
                    {attachment.path && (
                      <button
                        type='button'
                        className='btn btn-sm btn btn-light-success me-3'
                        onClick={() =>
                          attachment &&
                          downloadOnClickHandler &&
                          downloadOnClickHandler(attachment.id)
                        }
                      >
                        {attachment.processing ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-cloud-download fs-3'></i>
                        )}
                      </button>
                    )}

                    {attachment.error && (
                      <button
                        type='button'
                        className='btn btn-sm btn-light-warning me-3'
                        onClick={() =>
                          attachment && retryOnClickHandler && retryOnClickHandler(attachment.id)
                        }
                      >
                        {attachment.processing ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-arrow-clockwise fs-2'></i>
                        )}
                      </button>
                    )}
                  </>
                </div>
              )}

              <button type='button' className='btn btn-sm btn-light' onClick={closeOnClickHandler}>
                <i className='bi bi-x-lg'></i>
              </button>
            </div>

            <div>
              <div className='separator mt-5'></div>
              <div className='row mt-10'>
                <div className='px-5'>
                  <div className='row'>
                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.FILE_NAME'})}
                      value={truncateDescription(55, attachment.fileName.toUpperCase())}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.SIZE'})}
                      value={`${sizeFormater(attachment.size)}`}
                    ></CustomInputRenderer>

                    <CustomInputRenderer
                      label={intl.formatMessage({id: 'UTIL.CONTENT_TYPE'})}
                      value={attachment.contentType.toUpperCase()}
                    ></CustomInputRenderer>

                    {renderTextArea(attachment, formik, fieldName)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export {FilePickedPreview}
